import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, CodeBlock, Code, Steps, Step } from '../../components/DocComponents'

const toc = [
  { id: 'prometheus',  title: 'Connecting Prometheus',  level: 2 as const },
  { id: 'grafana',     title: 'Connecting Grafana',      level: 2 as const },
  { id: 'metrics-ref', title: 'Metrics reference',       level: 2 as const },
  { id: 'datadog',     title: 'DataDog / New Relic',     level: 2 as const },
  { id: 'custom',      title: 'Adding custom metrics',   level: 2 as const },
]

export function Metrics() {
  return (
    <Article toc={toc}>
      <H1>Metrics</H1>
      <Lead>
        All Rabbit services expose Prometheus-format metrics at <Code>/metrics</Code>. Any
        Prometheus-compatible system — Grafana Cloud, DataDog, New Relic, Victoria Metrics —
        can scrape these endpoints directly.
      </Lead>

      <H2 id="prometheus">Connecting Prometheus</H2>
      <P>
        Add a scrape job for each service you want to monitor. The minimal configuration scrapes
        all 8 services:
      </P>
      <CodeBlock language="yaml" title="prometheus.yml — scrape configuration">
{`global:
  scrape_interval: 15s

scrape_configs:
  - job_name: auth-service
    static_configs:
      - targets: ['localhost:6000']   # adjust host as needed
    metrics_path: /metrics

  - job_name: brainer
    static_configs:
      - targets: ['localhost:3005']
    metrics_path: /metrics

  - job_name: storage-service
    static_configs:
      - targets: ['localhost:7000']
    metrics_path: /metrics

  - job_name: buzz
    static_configs:
      - targets: ['localhost:3000']
    metrics_path: /metrics

  - job_name: mail-service
    static_configs:
      - targets: ['localhost:5000']
    metrics_path: /metrics

  - job_name: raider-agent
    static_configs:
      - targets: ['localhost:3010']
    metrics_path: /metrics

  - job_name: order-parser
    static_configs:
      - targets: ['localhost:3011']
    metrics_path: /metrics

  - job_name: lens
    static_configs:
      - targets: ['localhost:3030']
    metrics_path: /metrics`}
      </CodeBlock>
      <Callout type="tip">
        The bundled <Code>observability/prometheus.yml</Code> uses <Code>host.docker.internal</Code>
        as the target host so Prometheus (running in Docker) can reach services running on the host.
        In a Kubernetes deployment, replace with the service DNS name or use a ServiceMonitor CRD.
      </Callout>

      <H2 id="grafana">Connecting Grafana</H2>
      <Steps>
        <Step n={1} title="Add Prometheus as a datasource">
          <p>Navigate to <strong>Connections → Data Sources → Add</strong>. Choose <strong>Prometheus</strong>. Set URL to your Prometheus address (e.g. <Code>http://prometheus:9090</Code>) and click <strong>Save & Test</strong>.</p>
        </Step>
        <Step n={2} title="Import the pre-built dashboard">
          <p>Go to <strong>Dashboards → Import</strong>. Upload <Code>observability/grafana/dashboards/rabbit-overview.json</Code> from the repo, or paste its contents into the JSON field. The dashboard auto-selects the Prometheus datasource.</p>
        </Step>
        <Step n={3} title="Add Jaeger as a second datasource (optional)">
          <p>Navigate to <strong>Connections → Data Sources → Add</strong>. Choose <strong>Jaeger</strong>. Set URL to <Code>http://jaeger:16686</Code>. This enables trace links directly inside Grafana panels.</p>
        </Step>
      </Steps>
      <P>
        The bundled <Code>docker compose -f docker-compose.observability.yml up</Code> performs
        all three steps automatically via Grafana provisioning files.
      </P>

      <H2 id="metrics-ref">Metrics reference</H2>
      <H3>HTTP metrics (all Node.js services)</H3>
      <CodeBlock language="promql" title="Key PromQL expressions">
{`# Request rate per service (req/s)
sum by (job) (rate(http_request_duration_ms_count[1m]))

# Error rate (5xx) per service
sum by (job) (rate(http_request_duration_ms_count{status=~"5.."}[1m]))

# p95 latency per service
histogram_quantile(0.95,
  sum by (job, le) (rate(http_request_duration_ms_bucket[1m]))
)

# p99 latency per service
histogram_quantile(0.99,
  sum by (job, le) (rate(http_request_duration_ms_bucket[1m]))
)

# Latency for a specific route
histogram_quantile(0.95,
  sum by (route, le) (rate(http_request_duration_ms_bucket{job="brainer"}[1m]))
)`}
      </CodeBlock>
      <H3>Node.js runtime metrics (all Node.js services)</H3>
      <CodeBlock language="promql" title="Runtime PromQL expressions">
{`# Heap used (MB)
nodejs_heap_size_used_bytes / 1024 / 1024

# Event loop lag (ms) — high values indicate CPU starvation
nodejs_eventloop_lag_seconds * 1000

# Active handles (open connections, timers, etc.)
nodejs_active_handles_total

# GC pause duration
rate(nodejs_gc_duration_seconds_sum[1m])
  / rate(nodejs_gc_duration_seconds_count[1m])`}
      </CodeBlock>
      <H3>Python / lens metrics</H3>
      <CodeBlock language="promql" title="Lens PromQL expressions">
{`# Request rate
sum(rate(http_requests_total{job="lens"}[1m]))

# Latency p95
histogram_quantile(0.95,
  rate(http_request_duration_highr_seconds_bucket{job="lens"}[1m])
)`}
      </CodeBlock>
      <Callout type="note">
        Lens uses <Code>prometheus-fastapi-instrumentator</Code> which names its metrics
        <Code>http_requests_total</Code> and <Code>http_request_duration_highr_seconds</Code>
        — different from the Node.js histogram name <Code>http_request_duration_ms</Code>.
      </Callout>

      <H2 id="datadog">DataDog / New Relic / Grafana Cloud</H2>
      <P>
        Any agent that can scrape Prometheus endpoints works out of the box. Examples:
      </P>
      <CodeBlock language="yaml" title="DataDog Agent — prometheus check (datadog.yaml)">
{`init_config:

instances:
  - prometheus_url: http://localhost:6000/metrics
    namespace: rabbit
    metrics:
      - http_request_duration_ms_bucket
      - http_request_duration_ms_count
      - nodejs_heap_size_used_bytes
    type_overrides:
      http_request_duration_ms_bucket: gauge`}
      </CodeBlock>
      <CodeBlock language="yaml" title="New Relic — Prometheus Remote Write (prometheus.yml)">
{`remote_write:
  - url: https://metric-api.newrelic.com/prometheus/v1/write?prometheus_server=rabbit
    authorization:
      credentials: <NEW_RELIC_LICENSE_KEY>`}
      </CodeBlock>
      <CodeBlock language="bash" title="Grafana Cloud — alloy agent scrape (config.alloy)">
{`prometheus.scrape "rabbit" {
  targets = [
    { "__address__" = "localhost:6000", "job" = "auth-service" },
    { "__address__" = "localhost:3005", "job" = "brainer" },
    { "__address__" = "localhost:7000", "job" = "storage-service" },
    { "__address__" = "localhost:3000", "job" = "buzz" },
    { "__address__" = "localhost:5000", "job" = "mail-service" },
    { "__address__" = "localhost:3010", "job" = "raider-agent" },
    { "__address__" = "localhost:3011", "job" = "order-parser" },
    { "__address__" = "localhost:3030", "job" = "lens" },
  ]
  forward_to = [prometheus.remote_write.grafana_cloud.receiver]
}

prometheus.remote_write "grafana_cloud" {
  endpoint {
    url = "https://prometheus-prod-XX.grafana.net/api/prom/push"
    basic_auth {
      username = "<GRAFANA_CLOUD_METRICS_ID>"
      password = "<GRAFANA_CLOUD_API_KEY>"
    }
  }
}`}
      </CodeBlock>

      <H2 id="custom">Adding custom metrics</H2>
      <P>
        Each service's <Code>metrics.ts</Code> (or <Code>infrastructure/metrics.ts</Code> for
        brainer) exports the shared <Code>register</Code>. Import it to add service-specific
        counters or gauges:
      </P>
      <CodeBlock language="typescript" title="Example: active IMAP connections gauge in mail-service">
{`import { register, Gauge } from 'prom-client';

const activeMailboxConnections = new Gauge({
  name: 'mail_active_imap_connections',
  help: 'Number of currently active IMAP connections',
  registers: [register],
});

// call in your monitoring loop:
activeMailboxConnections.set(connectionCount);`}
      </CodeBlock>
      <P>
        The metric will appear in <Code>GET /metrics</Code> automatically and be scraped by
        Prometheus on its next scrape interval.
      </P>
    </Article>
  )
}
